import { LUDUM_ERROR_CODES, LUDUM_USER_ROLES, LUDUM_USER_STATUSES } from '@/constants';
const { ATHLETE, HEAD_COACH, COACH, MANAGER } = LUDUM_USER_ROLES;
const { APPROVED, PENDING, INVITED } = LUDUM_USER_STATUSES;
import get from 'get-object-path';
import router from '@/router';
import { ROUTES_HOME } from '@/constants/routes';
import { db, fb } from '@/firebase-config';
import i18n from '@/lang';
import { isWaterSession } from '@/helpers/sessionHelper';

export const user = {
    state: {
        subscribed: false,
        user: {},
        clubs: [],
        errorCode: null,
        selectedClubId: null,
        userUpdates: {},
        firebaseEnabled: false,
        lastJSUpdate: null
    },

    getters: {
        clubId(state, getters) {
            let clubId = state.selectedClubId;

            if (!clubId) {
                clubId = Number(Vue.cookie.get('selectedClub'));
            }

            if (!clubId && getters.userActiveClubs.length) {
                clubId = getters.userActiveClubs[0].club.data.id;
            }

            return clubId;
        },

        selectedClub(state, getters) {
            return state.clubs.find(o => o.club_id === getters.clubId);
        },

        isSelectedClubIndividual(state, getters) {
            return get(getters.selectedClub, 'club.data.is_individual');
        },

        selectedClubDefaultSportId(state, getters) {
            return get(getters.selectedClub, 'club.data.default_sport.data.id');
        },

        isClubDefaultSportWater(state, getters) {
            return isWaterSession(getters.selectedClubDefaultSportId);
        },

        hasBillingPermissionInSelectedClub(state, getters) {
            const permissions = get(getters.selectedClub, 'permissions.data') || [];
            return !!permissions.find(permission => get(permission, 'permission.data.id') === 'manage-licences');
        },

        isCurrentUserCoachOfSelectedClub(store, getters) {
            const clubRoles = get(getters.selectedClub, 'roles.data') || [];
            return clubRoles.some(({ id }) => [HEAD_COACH, COACH].includes(id));
        },

        isCurrentUserAthleteOfSelectedClub(store, getters) {
            const clubRoles = get(getters.selectedClub, 'roles.data') || [];
            return clubRoles.some(({ id }) => id === ATHLETE);
        },

        isCurrentUserClubManagerOfSelectedClub(store, getters) {
            const clubRoles = get(getters.selectedClub, 'roles.data') || [];
            return clubRoles.some(({ id }) => id === MANAGER);
        },

        selectedClubName(state, getters) {
            return get(getters.selectedClub, 'club.data.name');
        },

        userIndividualClub(state, getters) {
            return getters.userActiveClubs.find(club => get(club, 'club.data.is_individual'));
        },

        userActiveClubs(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === APPROVED);
        },

        userPendingClubs(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === PENDING);
        },

        userInvitedClubs(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === INVITED);
        },
        subscriptionHasExpired(state) {
            return state.errorCode === LUDUM_ERROR_CODES.SUBSCRIPTION_EXPIRED;
        },
        userHasNoClubs(state) {
            return state.errorCode === LUDUM_ERROR_CODES.NO_CLUBS;
        },
        userAllRoles(state) {
            return state.clubs.map(club => get(club, 'roles.data[0].name')).join(',');
        },
        firebaseEnabled(state) {
            return state.firebaseEnabled;
        },
        userUpdates(state) {
            return state.userUpdates;
        }
    },

    mutations: {
        setUserUpdates(state, data) {
            state.userUpdates = data;
        },

        updateFirebaseStatus(state, data) {
            state.firebaseEnabled = data;
        },

        userClubs(state, data) {
            state.clubs = _.sortBy(data, [o => o.club.data.name]);
        },

        updateErrorCode(state, data) {
            state.errorCode = data;
        },
        updateJSTimestamp(state, timestamp) {
            state.lastJSUpdate = timestamp;
        }
    },

    actions: {
        emptyClubs(context) {
            context.commit('userClubs', []);
        },

        updateErrorCode(context, value) {
            context.commit('updateErrorCode', value);
        },

        fetchUserClubs(context, forceLoad = false) {
            if (context.state.clubs.length > 0 && !forceLoad) {
                return;
            }
            return context
                .dispatch('get', {
                    url: api + 'my-clubs',
                    params: {
                        include:
                            'club,club.venue,' +
                            'club.creator:fields(email),' +
                            'club.subscription_info.subscription_plan.features,' +
                            'roles,permissions.permission:fields(id|name)'
                    }
                })
                .then(response => {
                    context.commit('userClubs', response.data);
                    context.dispatch('ensureClubIsSelected');
                    return response.data;
                });
        },

        ensureClubIsSelected(context) {
            const clubId = parseInt(Vue.cookie.get('selectedClub'));

            if (!clubId || !context.getters.userActiveClubs.find(({ club_id }) => club_id === clubId)) {
                if (context.getters.userIndividualClub) {
                    context.dispatch('selectClub', context.getters.userIndividualClub.club.data);
                } else {
                    if (context.getters.userActiveClubs.length) {
                        context.dispatch('selectClub', context.getters.userActiveClubs[0].club.data);
                    }
                }
            }
        },

        selectClub(context, payload) {
            Vue.cookie.set('selectedClub', payload.id, { expires: '30D' });
            context.state.selectedClubId = payload.id;

            const savedState = JSON.parse(localStorage.getItem('ludumGoToState'));
            if (savedState) {
                localStorage.removeItem('ludumGoToState');
            }

            const goToState = savedState || { name: ROUTES_HOME };
            router.push(goToState).catch(() => {});
        },

        checkToken(context) {
            return context
                .dispatch('get', {
                    url: `${api}validate-token?club_id=${get(context.getters, 'selectedClub.club_id')}`
                })
                .then(response => {
                    return response;
                })
                .catch(error => {
                    return error;
                });
        },

        async getFirebaseUserData(context, uid) {
            return db
                .collection('users')
                .doc(uid)
                .get();
        },

        processFirebaseUpdates(context, argUser) {
            const userUpdates = context.getters.userUpdates;
            const user = argUser.data();
            if (user.clubs_updated_at > userUpdates.clubs_updated_at) {
                context.dispatch('fetchUserClubs', true);
            }
            if (user.user_updated_at > userUpdates.user_updated_at) {
                context.dispatch('clubHelper/getMe');
            }
            if (user.notifications_updated_at > userUpdates.notifications_updated_at) {
                context.dispatch('clubHelper/getNumberOfUnreadNotifications');
            }
            context.commit('setUserUpdates', user);
        },

        async subscribeToUpdates(context, payload) {
            if (this.subscribed) {
                return;
            }
            const { email, firebase_password } = payload;
            try {
                const { user } = await fb.auth().signInWithEmailAndPassword(email, firebase_password);
                this.subscribed = true;
                window.addEventListener('online', () => {
                    db.collection('users')
                        .doc(user.uid)
                        .get()
                        .then(argUser => {
                            return this.dispatch('processFirebaseUpdates', argUser);
                        });
                });
                context.commit('updateFirebaseStatus', true);
                db.collection('users')
                    .doc(user.uid)
                    .onSnapshot(argUser => {
                        this.dispatch('processFirebaseUpdates', argUser);
                    });
                db.collection('app')
                    .doc('meta')
                    .onSnapshot(snapshot => {
                        const lastTimestamp = context.state.lastJSUpdate;
                        const { js_updated_at } = snapshot.data();
                        if (!lastTimestamp) {
                            return context.commit('updateJSTimestamp', js_updated_at);
                        }
                        if (lastTimestamp < js_updated_at) {
                            context.dispatch(
                                'notifyWarning',
                                {
                                    message: `${i18n.t(
                                        'webAppUpdated'
                                    )} <a href="#" onclick="window.location.reload(true); return false">${i18n.t(
                                        'webAppUpdatedReload'
                                    )}</a>.`,
                                    persist: true
                                },
                                { root: true }
                            );
                        }
                    });
            } catch {
                context.commit('updateFirebaseStatus', false);
            }
        }
    }
};

export default user;
